import type { ReactNode } from 'react';
import { GoogleIcon, LockIcon } from './icons';

/**
 * Why the person is looking at this screen instead of the workspace.
 *
 * `kind` is either an OAuth failure code from the callback or a session
 * verdict from /api/me. They share one field because, from here, they are one
 * question: is this a door that will open on the next click, or one that will
 * not open for this account at all?
 */
export interface LoginDenial {
  kind: string;
  /** The account we actually saw, when a policy turned it away. */
  email?: string;
  /** Who to ask for access, if the deploy configured someone. */
  contact?: string;
}

/**
 * A refusal by policy is not an error the person can retry their way out of,
 * so it gets its own screen rather than a red box above an unchanged button.
 * `domain_not_allowed` is the pre-per-person code, kept so an old redirect
 * still lands somewhere sensible.
 */
const BLOCKED = new Set(['not_allowed', 'domain_not_allowed', 'revoked']);

/** Everything else: transient, or the person's own choice to back out. */
const NOTICES: Record<string, { tone: 'notice' | 'error'; text: string }> = {
  expired: {
    tone: 'notice',
    text: 'Your session expired. Sign in again to pick up where you left off.',
  },
  access_denied: { tone: 'notice', text: 'Sign-in was cancelled.' },
  bad_state: {
    tone: 'error',
    text: 'That sign-in link went stale before it was used. Please try again.',
  },
  missing_code: { tone: 'error', text: 'Google did not finish the sign-in. Please try again.' },
  token_exchange_failed: { tone: 'error', text: 'Google rejected the sign-in. Please try again.' },
  no_id_token: { tone: 'error', text: 'Google returned no identity token. Please try again.' },
  no_email: { tone: 'error', text: 'That Google account exposes no email address.' },
  email_unverified: {
    tone: 'error',
    text: 'That Google account has no verified email address.',
  },
  callback_failed: { tone: 'error', text: 'Sign-in failed unexpectedly. Please try again.' },
};

function requestAccessHref(contact: string, email?: string): string {
  const subject = 'Cowork access request';
  const who = email ? `my Google account ${email}` : 'my Google account';
  const body = `Hi,\n\nCould you add ${who} to the Cowork allow list?\n\nThanks`;
  return `mailto:${contact}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function Blocked({ denial }: { denial: LoginDenial }) {
  const revoked = denial.kind === 'revoked';
  const { email, contact } = denial;

  return (
    <div className="login__card login__card--blocked">
      <div className="login__mark login__mark--blocked">
        <LockIcon />
      </div>

      <h1>{revoked ? 'Access removed' : 'Not on the list'}</h1>

      {email ? (
        <p className="login__subject">
          Signed in to Google as <span className="login__email">{email}</span>
        </p>
      ) : (
        <p className="login__subject">That Google account cannot open this workspace.</p>
      )}

      <p className="login__why">
        {revoked
          ? 'This account was signed in, and has since been taken off the Cowork allow list. Anything already sent to the agent is unaffected.'
          : 'Cowork drives a Claude agent that can read and write the team’s repositories, so access is granted one person at a time rather than to a whole domain.'}
      </p>

      <div className="login__actions">
        {contact ? (
          <>
            <a className="login__button" href={requestAccessHref(contact, email)}>
              Request access
            </a>
            <a className="login__button login__button--ghost" href="/auth/google">
              <GoogleIcon />
              Try a different account
            </a>
          </>
        ) : (
          <a className="login__button" href="/auth/google">
            <GoogleIcon />
            Try a different account
          </a>
        )}
      </div>

      <div className="login__hint">
        {contact ? (
          <>
            Access is managed by <span className="login__mono">{contact}</span>.
          </>
        ) : (
          'Ask whoever administers this workspace to add you.'
        )}
        {!revoked && ' Signed in to more than one Google account? The button above asks again.'}
      </div>
    </div>
  );
}

export function Login({ denial }: { denial?: LoginDenial }) {
  if (denial && BLOCKED.has(denial.kind)) return <Blocked denial={denial} />;

  const notice = denial?.kind ? NOTICES[denial.kind] : undefined;
  const unknown = denial?.kind && denial.kind !== 'anonymous' && !notice;

  return (
    <div className="login__card">
      <div className="login__mark">C</div>
      <h1>Cowork</h1>
      <p>One Claude session. Your whole team, watching and steering it together.</p>

      {notice && <div className={`login__notice login__notice--${notice.tone}`}>{notice.text}</div>}
      {unknown && (
        <div className="login__notice login__notice--error">
          Sign-in failed ({denial.kind}). Please try again.
        </div>
      )}

      <a className="login__button" href="/auth/google">
        <GoogleIcon />
        Continue with Google
      </a>

      <div className="login__hint">Restricted to approved company accounts.</div>
    </div>
  );
}

/** The page chrome, so App can render a bare backdrop while auth is checking. */
export function LoginShell({ children }: { children?: ReactNode }) {
  return <div className="login">{children}</div>;
}
